import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { differenceInYears, isFuture, isValid, parse } from 'date-fns';
import jm, { errorMessage } from '../api';
import SearchSelect, { departmentOptions, jobOptions, referrerOptions } from '../common/SearchSelect';
import DateField from '../common/DateField';
import NumberField from '../common/NumberField';

const EXPERIENCE_YEARS = Array.from({ length: 26 }, (_, i) => i);
const EXPERIENCE_MONTHS = Array.from({ length: 12 }, (_, i) => i);

/** Nobody under this may be taken on. */
const MIN_AGE = 18;

/** Kept in one place so the form and the API say the same thing. */
const MIN_AGE_MESSAGE =
  `A minimum age of ${MIN_AGE} years often required by government policy`;

/**
 * Reads a date of birth into { age, future }, or null when it will not parse.
 *
 * differenceInYears counts completed years, so a birthday still to come this
 * year leaves the younger age — and it returns 0 for a date a few months
 * ahead, which is why being in the future is asked separately rather than
 * inferred from a negative age.
 */
const readDob = (dob) => {
  const born = parse(dob || '', 'yyyy-MM-dd', new Date());
  if (!isValid(born)) return null;

  return { age: differenceInYears(new Date(), born), future: isFuture(born) };
};

const EMPTY = {
  first_name: '', last_name: '', dob: '', contact_no: '',
  gender: 'Male', marriage_status: 'Single', email: '', address: '',
  job_id: '', department_id: '', refered_by: '500', Referred_others: '',
  qualification: '', experience: '', month: '',
  current_salary: '', expected_salary: '', notes: '',
};

/**
 * The "Add New Candidate" accordion above the Applications list.
 * A new candidate always lands in Applications with job_status "Applied".
 */
const AddCandidateForm = ({ masters, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const resumeRef = useRef(null);
  const photoRef = useRef(null);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const enteredDob = readDob(form.dob);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const dob = readDob(form.dob);
    if (!dob) {
      toast.error('Please enter a valid date of birth', { toastId: 'candidate-age' });
      return;
    }
    if (dob.future) {
      toast.error('Date of birth cannot be in the future', { toastId: 'candidate-age' });
      return;
    }
    if (dob.age < MIN_AGE) {
      toast.error(
        `Candidate is ${dob.age} years old. ${MIN_AGE_MESSAGE}.`,
        { toastId: 'candidate-age' }
      );
      return;
    }

    const resume = resumeRef.current?.files?.[0];
    if (!resume) {
      toast.error('Resume is not uploaded');
      return;
    }

    setSaving(true);

    try {
      await jm.addCandidate({
        ...form,
        // The old app stored experience as "<years>.<months> Years"; keep that
        // shape. Month is optional, so an unpicked one counts as none.
        experience: `${form.experience || 0}.${form.month || 0} Years`,
        resume_file: resume,
        p_file: photoRef.current?.files?.[0],
      });

      toast.success('Candidate added');
      setForm(EMPTY);
      if (resumeRef.current) resumeRef.current.value = '';
      if (photoRef.current) photoRef.current.value = '';
      onDone();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} autoComplete="off">
      <h6 className="text-uppercase text-muted mb-3">Candidate Details</h6>
      <div className="row">
        <div className="col-md-6 form-group required-tooltip">
          <label>First Name</label>
          <input className="form-control required-input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} required />
        </div>
        <div className="col-md-6 form-group">
          <label>Last Name</label>
          <input className="form-control" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Date of Birth</label>
          <DateField value={form.dob} onChange={(v) => set('dob', v)} required />
          {/* Say it here too, so the rule shows up before the save is attempted. */}
          {enteredDob && (
            <small
              className={
                enteredDob.future || enteredDob.age < MIN_AGE ? 'text-danger' : 'text-muted'
              }
            >
              {enteredDob.future
                ? 'Date of birth is in the future'
                : enteredDob.age < MIN_AGE
                  ? `${enteredDob.age} years old — ${MIN_AGE_MESSAGE}`
                  : `${enteredDob.age} years old`}
            </small>
          )}
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Mobile No</label>
          <input
            className="form-control required-input"
            maxLength={10}
            value={form.contact_no}
            onChange={(e) => set('contact_no', e.target.value.replace(/\D/g, ''))}
            required
          />
        </div>
        <div className="col-md-6 form-group">
          <label>Gender</label>
          <select className="form-control" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div className="col-md-6 form-group">
          <label>Marital Status</label>
          <select className="form-control" value={form.marriage_status} onChange={(e) => set('marriage_status', e.target.value)}>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Widowed">Widowed</option>
            <option value="Divorced">Divorced or Separated</option>
          </select>
        </div>
        <div className="col-md-6 form-group">
          <label>Job Title</label>
          <SearchSelect
            value={form.job_id}
            onChange={(v) => set('job_id', v)}
            options={jobOptions(masters.jobs)}
            placeholder="Search job title…"
          />
        </div>
        <div className="col-md-6 form-group">
          <label>Department</label>
          <SearchSelect
            value={form.department_id}
            onChange={(v) => set('department_id', v)}
            options={departmentOptions(masters.departments)}
            placeholder="Search department…"
          />
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Resume</label>
          <input type="file" className="form-control-file required-input" ref={resumeRef} accept=".pdf,.doc,.docx" />
          <small className="text-muted">Upload only PDF or Word Doc</small>
        </div>
        <div className="col-md-6 form-group">
          <label>Photo</label>
          <input type="file" className="form-control-file" ref={photoRef} accept="image/*" />
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Referred By</label>
          <SearchSelect
            value={form.refered_by}
            onChange={(v) => set('refered_by', v || '500')}
            options={referrerOptions(masters.employees)}
            placeholder="Search employee…"
            isClearable={false}
            required
          />
        </div>
        {form.refered_by === '600' && (
          <div className="col-md-6 form-group">
            <label>Referred Other Person</label>
            <input className="form-control" value={form.Referred_others} onChange={(e) => set('Referred_others', e.target.value)} />
          </div>
        )}
      </div>

      <h6 className="text-uppercase text-muted mb-3">Address Details</h6>
      <div className="row">
        <div className="col-md-6 form-group required-tooltip">
          <label>Address</label>
          <textarea className="form-control required-input" rows="3" value={form.address} onChange={(e) => set('address', e.target.value)} required />
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Email</label>
          <input type="email" className="form-control required-input" value={form.email} onChange={(e) => set('email', e.target.value)} required />
        </div>
      </div>

      <h6 className="text-uppercase text-muted mb-3">Professional Details</h6>
      <div className="row">
        <div className="col-md-3 form-group required-tooltip">
          <label>Experience</label>
          <select className="form-control required-input" value={form.experience} onChange={(e) => set('experience', e.target.value)} required>
            <option value="">Select Experience</option>
            <option value="0">Fresher</option>
            {EXPERIENCE_YEARS.slice(1).map((year) => (
              <option key={year} value={year}>{year} year{year > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3 form-group">
          <label>Month</label>
          <select className="form-control" value={form.month} onChange={(e) => set('month', e.target.value)}>
            <option value="">Select Month</option>
            {EXPERIENCE_MONTHS.map((month) => (
              <option key={month} value={month}>{month} month{month > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
        <div className="col-md-6 form-group required-tooltip">
          <label>Qualification</label>
          <input className="form-control required-input" value={form.qualification} onChange={(e) => set('qualification', e.target.value)} required />
        </div>
        <div className="col-md-6 form-group">
          <label>Current Salary</label>
          <NumberField value={form.current_salary} onChange={(v) => set('current_salary', v)} />
        </div>
        <div className="col-md-6 form-group">
          <label>Expected Salary</label>
          <NumberField value={form.expected_salary} onChange={(v) => set('expected_salary', v)} />
        </div>
        <div className="col-md-12 form-group">
          <label>Remark</label>
          <textarea className="form-control" rows="2" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>
      </div>

      <button type="submit" className="btn btn-primary" disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
};

export default AddCandidateForm;
